import { Controller } from '@hotwired/stimulus'
import { resizeHeadshot } from '../../utils/resizeHeadshot'

// Coach headshots and coachee profile photos share this widget.
// Defaults keep the coach field name and response key.
type UploadJson = {
  error?: string
  [key: string]: string | null | undefined
}

function csrfToken(): string {
  const meta = document.querySelector("meta[name='csrf-token']")
  return meta?.getAttribute('content') ?? ''
}

async function parseJson(response: Response): Promise<UploadJson | null> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

function responseValue(data: UploadJson | null, key: string): string | null {
  const value = data?.[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

export default class HeadshotController extends Controller {
  static readonly targets = ['preview', 'input', 'status', 'error', 'uploadLabel', 'placeholder', 'remove']

  static readonly values = {
    uploadUrl: String,
    fieldName: { type: String, default: 'headshot' },
    responseKey: { type: String, default: 'headshotURL' },
  }

  declare previewTarget: HTMLImageElement

  declare inputTarget: HTMLInputElement

  declare statusTarget: HTMLElement

  declare errorTarget: HTMLElement

  declare uploadLabelTarget: HTMLElement

  declare placeholderTarget: HTMLElement

  declare readonly hasPlaceholderTarget: boolean

  declare removeTarget: HTMLButtonElement

  declare readonly hasRemoveTarget: boolean

  declare uploadUrlValue: string

  declare fieldNameValue: string

  declare responseKeyValue: string

  fileChosen() {
    const file = this.inputTarget.files?.[0]
    if (file) this.uploadFile(file)
  }

  async uploadFile(file: File) {
    this.clearError()
    this.setBusy(true)
    this.statusTarget.textContent = 'Preparing photo…'

    let prepared: File
    try {
      prepared = await resizeHeadshot(file)
    } catch (error) {
      const rejected = error instanceof Error && error.message.includes('JPEG or PNG')
      this.showError(rejected ? error.message : 'Could not prepare that photo. Please choose a JPEG or PNG.')
      this.setBusy(false)
      this.inputTarget.value = ''
      return
    }

    this.statusTarget.textContent = `Uploading ${prepared.name}…`

    const token = csrfToken()
    const body = new FormData()
    body.append(this.fieldNameValue, prepared)
    body.append('authenticity_token', token)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': token,
        },
        credentials: 'same-origin',
      })
      const data = await parseJson(response)
      if (!response.ok) {
        this.showError(data?.error || 'Could not upload photo. Please try again.')
        return
      }
      const url = responseValue(data, this.responseKeyValue)
      if (url) {
        this.previewTarget.src = url
        this.revealPreview()
      }
      this.statusTarget.textContent = 'Photo updated.'
    } catch {
      this.showError('Could not upload photo. Please try again.')
    } finally {
      this.setBusy(false)
      this.inputTarget.value = ''
    }
  }

  async removePhoto(event: Event) {
    event.preventDefault()
    this.clearError()
    this.setBusy(true)
    this.statusTarget.textContent = 'Removing photo…'

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        credentials: 'same-origin',
      })
      const data = await parseJson(response)
      if (!response.ok) {
        this.showError(data?.error || 'Could not remove photo. Please try again.')
        return
      }
      this.hidePreview()
      this.statusTarget.textContent = 'Photo removed.'
    } catch {
      this.showError('Could not remove photo. Please try again.')
    } finally {
      this.setBusy(false)
    }
  }

  // Coachee photos show initials until a picture exists. Coach headshots have no placeholder.
  revealPreview() {
    if (this.hasPlaceholderTarget) {
      this.previewTarget.classList.remove('hidden')
      this.placeholderTarget.classList.add('hidden')
    }
    if (this.hasRemoveTarget) this.removeTarget.classList.remove('hidden')
  }

  hidePreview() {
    this.previewTarget.classList.add('hidden')
    this.previewTarget.removeAttribute('src')
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden')
    if (this.hasRemoveTarget) this.removeTarget.classList.add('hidden')
  }

  setBusy(busy: boolean) {
    this.inputTarget.disabled = busy
    if (this.hasRemoveTarget) this.removeTarget.disabled = busy
    this.uploadLabelTarget.textContent = busy ? 'Please wait…' : 'Upload photo'
  }

  showError(message: string) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
    this.statusTarget.textContent = ''
  }

  clearError() {
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }
}
